import React from "react";
import { useTheme, alpha } from "@mui/material/styles";
import { Box, Typography, Button, IconButton, Tooltip } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import * as Icons from "@mui/icons-material";
import { createCustomerPanelUi, CUSTOMER_PANEL_ACCENTS } from "./customerPanelStyles";

// Render icono dinámico (temporadas + estados)
const getIcon = (nombreIcono, color) => {
  const IconComponent = Icons[nombreIcono];
  if (!IconComponent) return null;
  return <IconComponent sx={{ color }} />;
};

const Projects = ({
  proyectos = [],
  marcas = [],
  temporadas = [],
  estados = [],
  isDeletionAllowed,
  handleEditProyecto,
  handleDeleteProyecto,
  setDrawerOpen,
  canAdd,
  COLUMN_WIDTH,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const accent = CUSTOMER_PANEL_ACCENTS.projects;
  const safeProyectos = Array.isArray(proyectos)
    ? proyectos.filter((item) => item && typeof item === "object")
    : [];
  const safeMarcas = Array.isArray(marcas)
    ? marcas.filter((item) => item && typeof item === "object")
    : [];
  const safeTemporadas = Array.isArray(temporadas)
    ? temporadas.filter((item) => item && typeof item === "object")
    : [];
  const safeEstados = Array.isArray(estados)
    ? estados.filter((item) => item && typeof item === "object")
    : [];
  const ui = createCustomerPanelUi(theme, accent);
  const panelSx = {
    ...ui.panelSx,
    width: COLUMN_WIDTH,
    minWidth: COLUMN_WIDTH,
    maxWidth: COLUMN_WIDTH,
  };
  const primaryButtonSx = ui.addButtonSx;
  const actionIconSx = {
    ...ui.actionIconSx,
  };
  const toSafeText = (value, fallback = "-") => {
    if (value == null || value === "") return fallback;
    return typeof value === "string" ? value : String(value);
  };
  const getSafeColor = (value, fallback) => {
    const candidate = typeof value === "string" ? value.trim() : "";
    return candidate || fallback;
  };
  const getSafeAlpha = (value, opacity, fallback) => {
    try {
      return alpha(getSafeColor(value, fallback), opacity);
    } catch {
      return alpha(fallback, opacity);
    }
  };
  const formatSafeNumber = (value) => {
    const numberValue = Number(value);
    return Number.isFinite(numberValue)
      ? numberValue.toLocaleString("de-DE")
      : "0";
  };
  const formatSafeDate = (value) => {
    if (!value) return "-";
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime())
      ? "-"
      : parsed.toLocaleDateString("es-ES");
  };

  return (
    <Box sx={panelSx}>
      {/* HEADER */}
      <Box sx={{ ...ui.headerSx, pl: 2 }}>
        <Box sx={{ minWidth: 0, flex: 1, pr: 1 }}>
          <Typography sx={ui.headerEyebrowSx}>
            {t("Projects.Projects")}
          </Typography>
          <Typography sx={ui.headerTitleSx}>
            {safeProyectos.length}{" "}
            {safeProyectos.length === 1 ? "proyecto" : "proyectos"}
          </Typography>
        </Box>

        <Button
          variant="outlined"
          disabled={!canAdd}
          sx={primaryButtonSx}
          onClick={() => {
            handleEditProyecto(null);
            setDrawerOpen(true);
          }}
        >
          {t("Projects.Add")}
        </Button>
      </Box>

      {/* CONTENIDO */}
      <Box sx={ui.bodySx}>
        {safeProyectos.length === 0 ? (
          <Typography sx={ui.emptyStateSx}>
            {t("Projects.NoProjects")}
          </Typography>
        ) : (
          safeProyectos.map((proyecto) => {
            const marca = safeMarcas.find((m) => m.ID_MARCA === proyecto.marcaId);
            const estado = safeEstados.find((e) => e.NOMBRE === proyecto.status);
            const temporada = safeTemporadas.find(
              (t) => t.TEMPORADA === proyecto.proyectType
            );
            const temporadaColor = getSafeColor(temporada?.COLOR_HEX, accent);
            const estadoColor = getSafeColor(estado?.COLOR_HEX, "#00c2a3");

            return (
              <Box
                key={proyecto.id}
                sx={{
                  ...ui.cardSx,
                  padding: "0.75rem",
                  mb: 1,
                  position: "relative",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                    gap: 0.75,
                  }}
                >
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography
                      sx={{
                        fontWeight: 800,
                        color: "text.primary",
                        fontSize: "0.9rem",
                        lineHeight: 1.15,
                      }}
                    >
                      {toSafeText(proyecto.subject)}
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 0.45,
                        mt: 0.55,
                      }}
                    >
                      <Box
                        sx={{
                          ...ui.pillSx,
                          fontSize: "0.64rem",
                          color: "#FFAC7E",
                          textTransform: "uppercase",
                        }}
                      >
                        {marca ? marca.MARCA : t("Projects.SinMarca")}
                      </Box>
                      <Box
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 0.3,
                          ...ui.pillSx,
                          borderColor: getSafeAlpha(temporadaColor, 0.2, accent),
                          backgroundColor: getSafeAlpha(temporadaColor, 0.08, accent),
                          fontSize: "0.64rem",
                        }}
                      >
                        {temporada && getIcon(temporada.ICONO, temporadaColor)}
                        {toSafeText(proyecto.proyectType)}
                      </Box>
                      <Box
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 0.3,
                          ...ui.pillSx,
                          borderColor: getSafeAlpha(estadoColor, 0.2, "#00c2a3"),
                          backgroundColor: getSafeAlpha(estadoColor, 0.08, "#00c2a3"),
                          fontSize: "0.64rem",
                        }}
                      >
                        {estado && getIcon(estado.ICONO, estadoColor)}
                        {toSafeText(proyecto.status)}
                      </Box>
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      gap: 0.45,
                      ml: 0.35,
                    }}
                  >
                    <IconButton
                      onClick={() => handleEditProyecto(proyecto)}
                      aria-label="edit"
                      sx={{
                        ...actionIconSx,
                        borderColor: alpha("#34afe2", 0.24),
                      }}
                    >
                      {getIcon("CreateOutlined", "#34afe2")}
                    </IconButton>
                    <Tooltip title="Eliminar proyecto">
                      <IconButton
                        disabled={!isDeletionAllowed}
                        onClick={() => handleDeleteProyecto(proyecto.id)}
                        sx={{
                          ...actionIconSx,
                          borderColor: alpha(theme.palette.error.main, 0.24),
                        }}
                      >
                        {getIcon("DeleteOutlineOutlined", theme.palette.error.main)}
                      </IconButton>
                    </Tooltip>

                    <Tooltip title="Ver tareas del proyecto">
                      <IconButton
                        onClick={() =>
                          navigate(`/proyectos/${proyecto.id}/tareas`)
                        }
                        sx={{
                          ...actionIconSx,
                          borderColor: alpha("#00c2a3", 0.24),
                        }}
                      >
                        {getIcon("ListAlt", "#00c2a3")}
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>

                {proyecto.notes ? (
                  <Typography
                    sx={{
                      mt: 0.85,
                      color: "text.secondary",
                      fontSize: "0.78rem",
                      lineHeight: 1.45,
                    }}
                  >
                    {toSafeText(proyecto.notes, "")}
                  </Typography>
                ) : null}

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                    gap: 0.55,
                    mt: 0.9,
                  }}
                >
                  <Box
                    sx={{
                      ...ui.metricTileSx,
                      borderColor: alpha("#34afe2", 0.12),
                      backgroundColor: alpha("#34afe2", 0.07),
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "0.6rem",
                        fontWeight: 800,
                        textTransform: "uppercase",
                        letterSpacing: 0.5,
                        color: "text.secondary",
                      }}
                    >
                      Prev
                    </Typography>
                    <Typography sx={{ mt: 0.15, fontWeight: 800, fontSize: "0.78rem" }}>
                      € {formatSafeNumber(proyecto.previsionVenta)}
                    </Typography>
                    <Typography sx={{ fontSize: "0.74rem", color: "#34afe2", fontWeight: 700 }}>
                      U: {formatSafeNumber(proyecto.previsionPrendas)}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      ...ui.metricTileSx,
                      borderColor: alpha("#00c2a3", 0.12),
                      backgroundColor: alpha("#00c2a3", 0.07),
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "0.6rem",
                        fontWeight: 800,
                        textTransform: "uppercase",
                        letterSpacing: 0.5,
                        color: "text.secondary",
                      }}
                    >
                      Real
                    </Typography>
                    <Typography sx={{ mt: 0.15, fontWeight: 800, fontSize: "0.78rem" }}>
                      € {formatSafeNumber(proyecto.ventaFinal)}
                    </Typography>
                    <Typography sx={{ fontSize: "0.74rem", color: "#00c2a3", fontWeight: 700 }}>
                      U: {formatSafeNumber(proyecto.prendasFinal)}
                    </Typography>
                  </Box>
                </Box>

                <Typography
                  sx={{ mt: 0.75, color: "text.secondary", fontWeight: 700, fontSize: "0.74rem" }}
                >
                  {formatSafeDate(proyecto.createdAt)}
                </Typography>
              </Box>
            );
          })
        )}
      </Box>
    </Box>
  );
};

export default Projects;
